import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useAppStore } from "store";
import { matchPath, useHistory, useRouteMatch } from "react-router-dom";
import styles from "./styles.module.scss";
import classNames from "classnames";

import CmsCourses from "components/partial/cms/cms-courses/cms-courses";
import CmsThreads from "components/partial/cms/cms-threads/cms-threads";
import CmsUsers from "components/partial/cms/cms-users/cms-users";
import ProfileLayout from "layouts/private/profile/profile-layout";
import LoadingIndicator from "components/partial/loading-indicator/loading-indicator";
import GroupSettings from "./components/group-settings";

import SubMenu from "components/partial/submenu/submenu";
import { getGroupsMenuItems, GroupTabs } from "components/partial/submenu/items";
import { Routes } from "routes";
import { PermissionConstantsCreateOrUpdate } from "models/dto/ZoomiLxp/Utilities/Constants/PermissionConstantsCreateOrUpdate";
import { PermissionConstantsDelete } from "models/dto/ZoomiLxp/Utilities/Constants/PermissionConstantsDelete";
import { IGroupModel } from "models/dto/ZoomiLxp/Models/GroupModels/IGroupModel";
import { IFileModel } from "models/dto/ZoomiLxp/Models/Common/IFileModel";
import { GroupThresholdMode } from "models/dto/ZoomiLxp/Utilities/Enumerations/GroupThresholdMode";

const newGroupData: IGroupModel = {
	id: 0,
	name: "",
	displayName: "",
	description: "",
	price: 0,
	thumbnail: {} as IFileModel,
	autoAssignCourses: false,
	discoverableForLearners: false,
	enableExamThreshold: false,
	examThresholdMode: GroupThresholdMode.PerCourse,
	enableZ1Threshold: false,
	z1ThresholdMode: GroupThresholdMode.PerCourse,
	enableEngagementThreshold: false,
	engagementThresholdMode: GroupThresholdMode.PerCourse,
	enableProgressThreshold: false,
	enableCertification: false,
	courses: [],
};

const GroupProfilePage = () => {
	const { params } = useRouteMatch<{ id: string; tab: string }>();
	const history = useHistory();
	const { groupStore, usersStore } = useAppStore();
	const isNewGroup = !!matchPath(history.location.pathname, Routes.CmsAddGroup);
	const [isDataInitialised, setIsDataInitialised] = useState(isNewGroup);
	const [groupDisplayName, setGroupDisplayName] = useState(isNewGroup ? "Add Group" : groupStore.groupData.displayName);
	const isGroupUsersAllowed = usersStore.checkOneOfUserPermissions([
		PermissionConstantsDelete.GroupCmsManagement,
		PermissionConstantsCreateOrUpdate.AllAccounts,
	]);
	const groupFilter = params.tab ? params.tab : GroupTabs.Settings;
	const updateForm = (group: IGroupModel) => {
		groupStore.groupData = group;
		setGroupDisplayName(group.displayName);
	};

	useEffect(() => {
		if (isNewGroup) {
			setGroupDisplayName("Add Group");
			setIsDataInitialised(true);
			return;
		}

		(async () => {
			setIsDataInitialised(false);
			groupStore.isLoading = true;
			await groupStore.getGroupData(Number(params.id));
			if (groupFilter === GroupTabs.Users) {
				groupStore.updateUsersParams();
			} else if (groupFilter === GroupTabs.Threads) {
				groupStore.updateThreadsParams();
			} else if (groupFilter === GroupTabs.Courses) {
				groupStore.updateCourseParams();
			}

			setGroupDisplayName(groupStore.groupData.displayName);
			setIsDataInitialised(true);
			groupStore.isLoading = false;
		})();
	}, [groupFilter, groupStore, isNewGroup, params.id]);

	return (
		<ProfileLayout onBackButtonClick={() => history.push(Routes.CmsGroups)}>
			<div className={styles.group_profile}>
				<div className={styles.group_profile__tile_wrapper}>
					<div className={styles.group_profile__title}>{groupDisplayName}</div>
				</div>
				{!isNewGroup && (
					<div className={classNames(styles.group_profile__tile_wrapper, styles.group_profile__button_wrapper)}>
						<SubMenu
							className={styles.group_profile_layout__submenu}
							menuItems={getGroupsMenuItems(params.id, isGroupUsersAllowed)}
						/>
					</div>
				)}
				<LoadingIndicator loading={groupStore.isLoading} spinnerPosition="center" backgroundStyle={"white"}>
					{isDataInitialised && (
						<>
							{groupFilter === GroupTabs.Settings && (
								<GroupSettings
									group={isNewGroup ? newGroupData : groupStore.groupData}
									isNewGroup={isNewGroup}
									onSaved={updateForm}
								/>
							)}
							{groupFilter === GroupTabs.Courses && <CmsCourses isHideFilter enableGroupActions />}
							{groupFilter === GroupTabs.Threads && <CmsThreads isHideFilter />}
							{groupFilter === GroupTabs.Users && <CmsUsers isHideFilter enableGroupActions />}
						</>
					)}
				</LoadingIndicator>
			</div>
		</ProfileLayout>
	);
};

export default observer(GroupProfilePage);
